import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppShell,
  IconButton,
  IconBell,
  IconCalendar,
  IconCamp,
  IconCode,
  IconDocument,
  IconGame,
  IconGrip,
  IconHome,
  IconList,
  IconMail,
  IconPhoto,
  IconSearch,
  IconSettings,
  IconShield,
  IconShirt,
  IconTasks,
  IconUsers,
  IconWallet,
  ReportDialog,
  ShellUserMenu,
  Tooltip,
  useToast,
} from '@bala/ui';
import { displayName as toDisplayName } from '@bala/core';
import type { Resource } from '@bala/core';
import { api, useConnection, useQuery } from '../api';
import { useAuth } from '../auth';
import { openCampSso } from '../camp-sso';
import { openParentsSso } from '../parents-sso';
import { GlobalSearch } from './GlobalSearch';
import { NotificationPanel } from './NotificationPanel';

/** Un onglet de navigation, avec le domaine qui conditionne son affichage. */
interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: ReactNode;
  resource?: Resource;
  /** Si plusieurs domaines ouvrent le même onglet (ex. Liste). */
  anyOfResources?: Resource[];
  requiresBaygon?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Accueil', short: 'Accueil', icon: <IconHome size={20} /> },
  {
    to: '/liste',
    label: 'Liste',
    short: 'Liste',
    icon: <IconUsers size={20} />,
    resource: 'baladins',
    anyOfResources: ['baladins', 'animateurs'],
  },
  { to: '/calendrier', label: 'Calendrier', short: 'Agenda', icon: <IconCalendar size={20} />, resource: 'calendar' },
  { to: '/presences', label: 'Présences', short: 'Présences', icon: <IconList size={20} />, resource: 'attendance' },
  { to: '/taches', label: 'Tâches', short: 'Tâches', icon: <IconTasks size={20} />, resource: 'tasks' },
  { to: '/finances', label: 'Finances', short: 'Finances', icon: <IconWallet size={20} />, resource: 'finances' },
  { to: '/jeux', label: 'Jeux', short: 'Jeux', icon: <IconGame size={20} />, resource: 'games' },
  { to: '/inventaire', label: 'Inventaire', short: 'Matos', icon: <IconGrip size={20} />, resource: 'inventaire' },
  // Écrit en entier sur tous les formats. La barre du bas défile
  // horizontalement et ses onglets se dimensionnent à leur contenu : un mot
  // long y élargit son propre onglet, il ne déborde sur personne.
  { to: '/uniformes', label: 'Uniformes', short: 'Uniformes', icon: <IconShirt size={20} />, resource: 'uniformes' },
  { to: '/photos', label: 'Photos', short: 'Photos', icon: <IconPhoto size={20} />, resource: 'photos' },
  { to: '/objets-perdus', label: 'Objets perdus', short: 'Perdus', icon: <IconSearch size={20} />, resource: 'photos' },
  { to: '/documents', label: 'Documents', short: 'Docs', icon: <IconDocument size={20} />, resource: 'documents' },
  { to: '/communications', label: 'Comm Parents', short: 'Comm', icon: <IconMail size={20} />, resource: 'messages' },
  { to: '/baygon', label: 'Baygon', short: 'Baygon', icon: <IconShield size={20} />, requiresBaygon: true },
  { to: '/dev', label: 'Dev', short: 'Dev', icon: <IconCode size={20} />, requiresBaygon: true },
  // Sans `resource` : l'écran est ouvert à tout le staff, chacun y ayant son
  // thème, son mot de passe et le lien d'abonnement au calendrier. Ce sont les
  // sections engageant la section qui s'y masquent, pas la page entière.
  { to: '/parametres', label: 'Paramètres', short: 'Réglages', icon: <IconSettings size={20} /> },
];

export function Shell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const { state, pending } = useConnection();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const settings = useQuery<{
    settings: {
      sectionName: string;
      unitName: string;
      logoFileId: string | null;
      uniformesStaffEnabled?: boolean;
      objetsPerdusStaffEnabled?: boolean;
    };
  }>('/settings', ['settings']);
  const notifications = useQuery<{ unread: number }>('/notifications?unread=1', ['notification']);
  // Chemin `null` quand l'onglet est masqué : la route est réservée au Baygon,
  // l'appeler pour les autres ne ramènerait qu'un 403 à chaque changement diffusé.
  const devTickets = useQuery<{ unread: number }>(
    auth.isBaygon ? '/dev-tickets/unread' : null,
    ['dev_tickets'],
  );

  // Raccourci clavier de la recherche globale : ⌘K sur Mac, Ctrl+K ailleurs.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const uniformesEnabled = settings.data?.settings.uniformesStaffEnabled !== false;
  const objetsPerdusEnabled = settings.data?.settings.objetsPerdusStaffEnabled !== false;

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.to === '/uniformes' && !uniformesEnabled) return false;
    if (item.to === '/objets-perdus' && !objetsPerdusEnabled) return false;
    if (item.requiresBaygon) return auth.isBaygon;
    if (item.anyOfResources?.length) {
      return item.anyOfResources.some((resource) => auth.can(resource, 'read'));
    }
    if (item.resource) return auth.can(item.resource, 'read');
    return true;
  }).map((item) =>
    item.to === '/dev' ? { ...item, badge: devTickets.data?.unread ?? 0 } : item,
  );

  const sectionName = settings.data?.settings.sectionName ?? 'Baladins';
  const unitName = settings.data?.settings.unitName ?? '';
  const logoUrl = settings.data?.settings.logoFileId ? api.fileUrl(settings.data.settings.logoFileId) : null;
  const unread = notifications.data?.unread ?? 0;

  const currentTitle =
    location.pathname.startsWith('/ma-fiche')
      ? 'Ma fiche'
      : (visibleItems.find((item) => item.to !== '/' && location.pathname.startsWith(item.to))?.label ?? 'Accueil');

  return (
    <AppShell
      brand={{
        name: unitName || sectionName,
        sub: unitName ? `Section ${sectionName}` : null,
        emblemSrc: logoUrl ?? '/emblem.webp',
      }}
      navItems={visibleItems}
      title={currentTitle}
      pathname={location.pathname}
      linkComponent={NavLink}
      sync={{ state, pending }}
      userMenu={<UserMenu />}
      compactUserMenu={<UserMenu compact />}
      headerActions={
        <>
          {auth.isChefUnite ? (
            <>
              <ReadOnlyBadge />
              <OpenParentsHeaderButton />
            </>
          ) : null}
          {/* Le Chef d'Unité y entre aussi, en consultation. */}
          <Tooltip content={auth.isChefUnite ? 'Carnet de camp (consultation)' : 'Carnet de camp (Bala-Camp)'}>
            <IconButton label="Carnet de camp" tone="gray" onClick={() => void openCampSso()}>
              <IconCamp size={18} />
            </IconButton>
          </Tooltip>

          <Tooltip content="Rechercher (⌘K)">
            <IconButton label="Recherche globale" tone="gray" onClick={() => setSearchOpen(true)}>
              <IconSearch size={17} />
            </IconButton>
          </Tooltip>

          <div style={{ position: 'relative' }}>
            <IconButton
              label={unread > 0 ? `${unread} notification(s) non lue(s)` : 'Notifications'}
              tone="gray"
              onClick={() => setNotificationsOpen(true)}
            >
              <IconBell size={19} />
            </IconButton>
            {unread > 0 && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 'var(--r-full)',
                  background: 'var(--red)',
                  // Jeton et non blanc en dur : en thème sombre le rouge est
                  // clair, et le blanc y tombait sous le seuil AA.
                  color: 'var(--on-solid)',
                  font: '600 10px/16px var(--font)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </div>
        </>
      }
      overlays={
        <>
          <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
          <NotificationPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
        </>
      }
    >
      {children}
    </AppShell>
  );
}

/** Rappel permanent, dans l'en-tête, que ce compte ne peut rien modifier. */
function ReadOnlyBadge() {
  return (
    <span
      className="bala-readonly-badge"
      role="status"
      title="Votre compte Chef d'Unité consulte l'application sans pouvoir rien modifier."
    >
      <IconShield size={13} />
      <span className="bala-readonly-badge-long">Mode Lecture Seule (Chef d’Unité)</span>
      <span className="bala-readonly-badge-short">Lecture seule</span>
    </span>
  );
}

function OpenParentsHeaderButton() {
  const toast = useToast();
  return (
    <Tooltip content="Ouvrir l’app Parents">
      <IconButton
        label="Ouvrir l’app Parents"
        tone="gray"
        onClick={() => {
          openParentsSso().catch((error: unknown) => {
            toast.error(error instanceof Error ? error.message : "Impossible d'ouvrir l'application Parents.");
          });
        }}
      >
        <IconUsers size={18} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Menu du compte, réduit à ce qui est propre aux animateurs : l'identité lue
 * dans la session et les trois destinations. Toute l'apparence vient de
 * `ShellUserMenu`.
 */
function UserMenu({ compact = false }: { compact?: boolean }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [reporting, setReporting] = useState(false);
  if (!auth.user) return null;

  return (
    <>
      <ShellUserMenu
        compact={compact}
        name={toDisplayName(auth.user)}
        subtitle={auth.role?.name}
        avatarSrc={auth.user.avatarFileId ? api.fileUrl(auth.user.avatarFileId, 'thumb') : null}
        items={[
          { label: 'Ma fiche', onClick: () => navigate('/ma-fiche') },
          { label: 'Paramètres', onClick: () => navigate('/parametres') },
          ...(auth.isChefUnite
            ? []
            : [{ label: 'Signaler un problème', separated: true, onClick: () => setReporting(true) }]),
          {
            label: 'Se déconnecter',
            danger: true,
            separated: true,
            onClick: () => {
              void auth.signOut();
            },
          },
        ]}
      />
      <ReportDialog
        open={reporting}
        onClose={() => setReporting(false)}
        onSubmit={(report) => api.post('/dev-tickets', report).then(() => undefined)}
      />
    </>
  );
}
